<template>
  <div :class="controlClasses" class="mds-form-control mds-checkbox-group">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <VCheckbox
          v-for="(option, index) in options"
          :key="uid + '_' + index"
          :disabled="disabled"
          v-bind="checkboxProps(index)"
          @input="onInput($event, index)"
          @update:modelValue="onInput($event, index)"
        >
          {{ checkboxLabel(index) }}
        </VCheckbox>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VCheckboxGroup.scss" />

<script lang="ts">
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";

import VCheckbox from '@/lib/components/VCheckbox/VCheckbox.vue';

const CHECKBOX_LAYOUTS = ['horizontal', 'vertical', 'buttons'];

export default defineComponent({
  name: 'VCheckboxGroup',

  components: { VCheckbox },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** Опции для группы. Каждая может быть либо примитивом, либо объектом. */
    options: {
      type:    Array,
      default: () => [],
    },
    /** Поле объекта-опции для отображения */
    optionLabel: {
      type:    String,
      default: null,
    },
    /** Поле объекта-опции в качестве идентификатора */
    optionIdentity: {
      type:    String,
      default: null,
    },
    /** v-model */
    modelValue: {
      type:    [Array, Object, String, Number, Boolean],
      default: () => [],
    },
    /** Обратная совместимость с :value */
    value: {
      type:    [Array, Object, String, Number, Boolean],
      default: null,
    },
    /** Раскладка чекбоксов внутри группы */
    checkboxLayout: {
      type:      String,
      default:   'vertical',
      validator: (v: string) => CHECKBOX_LAYOUTS.includes(v),
    },
    /** Режим радио (выбор одного из) вместо чекбоксов */
    typeRadio: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'input'],

  data() {
    return {
      valueLocal: this.getInitialValue(),
    };
  },

  computed: {
    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      classes.push(`checkbox-layout-${this.checkboxLayout}`);
      if (this.typeRadio === true) classes.push('radio-button');
      return classes;
    },

    /** Приоритет value над modelValue (обратная совместимость). */
    currentValue() {
      if (this.value !== null) return this.value;
      return this.modelValue;
    },
  },

  watch: {
    currentValue(newVal, oldVal) {
      this.validation.markTouched();

      // Синхронизируем локальную копию значения.
      if (this.typeRadio === true) {
        this.valueLocal = newVal !== null ? newVal : null;
      } else if (newVal === null || Array.isArray(newVal) === false) {
        this.valueLocal = [];
      } else {
        this.valueLocal = [...newVal];
      }

      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validation.markDirty();
        this.validate();
      }
    },
  },

  methods: {
    /**
     * Возвращает начальное значение для valueLocal на основе value/modelValue.
     * Для типа radio — единичное значение или null, для чекбоксов — массив.
     */
    getInitialValue() {
      const sourceValue = this.value !== null ? this.value : this.modelValue;
      let value = sourceValue !== null && sourceValue !== undefined ? sourceValue : null;

      if (this.typeRadio === true) {
        return value;
      }

      if (value !== null && Array.isArray(value) === true && value.length > 0) {
        return [...value];
      }
      return [];
    },

    /**
     * Возвращает пропсы для конкретного дочернего VCheckbox.
     * Решает: какое значение должно быть отмечено в этом конкретном чекбоксе,
     * исходя из текущего valueLocal и типа группы (radio/checkbox).
     */
    checkboxProps(index) {
      const props: Record<string, unknown> = {
        modelValue:   false,
        checkedValue: null,
        type:         this.typeRadio === true ? 'radio' : 'checkbox',
      };

      if (this.typeRadio === true) {
        return this.radioCheckboxProps(props, index);
      }
      return this.multiCheckboxProps(props, index);
    },

    /** Пропсы для radio-режима. */
    radioCheckboxProps(props, index) {
      const option = this.options[index];

      if (this.optionIdentity !== null) {
        props.checkedValue = option[this.optionIdentity];
        props.modelValue = this.valueLocal === props.checkedValue ? props.checkedValue : null;
        return props;
      }

      props.checkedValue = option;
      props.modelValue = Core.utils.isEqual(this.valueLocal, option) === true ? option : null;
      return props;
    },

    /** Пропсы для checkbox-режима (множественный выбор). */
    multiCheckboxProps(props, index) {
      const option = this.options[index];

      if (this.optionIdentity !== null) {
        props.checkedValue = option[this.optionIdentity];
        const isSelected = this.valueLocal.findIndex((d) => Core.utils.isEqual(d, props.checkedValue)) !== -1;
        props.modelValue = isSelected === true ? props.checkedValue : null;
        return props;
      }

      props.checkedValue = option;
      const isSelected = this.valueLocal.findIndex((d) => Core.utils.isEqual(d, option)) !== -1;
      props.modelValue = isSelected === true ? option : null;
      return props;
    },

    checkboxLabel(index) {
      if (this.optionLabel !== null) {
        return this.options[index][this.optionLabel];
      }
      return this.options[index];
    },

    onInput(newValue, index) {
      if (this.typeRadio === true) {
        this.handleRadioInput(newValue);
      } else {
        this.handleCheckboxInput(newValue, index);
      }
    },

    /** Обработка ввода в radio-режиме — выбираем один вариант. */
    handleRadioInput(newValue) {
      if (newValue === null || Core.utils.isEqual(this.valueLocal, newValue) === true) return;

      this.$emit('update:modelValue', newValue);
      this.$emit('input', newValue);
    },

    /** Обработка ввода в checkbox-режиме — добавляем/убираем из массива. */
    handleCheckboxInput(newValue, index) {
      const value = [...this.valueLocal];
      const identity = this.optionIdentity !== null
        ? this.options[index][this.optionIdentity]
        : this.options[index];
      const indexLocal = value.findIndex((d) => Core.utils.isEqual(d, identity));

      if (newValue === null) {
        if (indexLocal > -1) value.splice(indexLocal, 1);
      } else {
        if (indexLocal === -1) value.push(identity);
      }

      this.$emit('update:modelValue', value);
      this.$emit('input', value);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.currentValue);
    },
  },
});
</script>
